import React, { useEffect, useState } from 'react';
import { BiMedal } from 'react-icons/bi';
import { FiSettings } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.less';

import {
  CloseOutlined,
  CreditCardOutlined,
  DollarOutlined,
  DownloadOutlined,
  GiftOutlined,
  HomeOutlined,
  OrderedListOutlined,
  ShoppingCartOutlined,
  StockOutlined,
  UserOutlined,
  UserSwitchOutlined,
  WalletOutlined,
} from '@ant-design/icons';

import checkOnboarding from '../../dashboard/checkOnboarding';
import {
  CompanyFactoryPrice,
  includesGujaratStateAndOther,
  secondaryPermission,
  UserTypeArr,
  UserTypeDict,
} from '../../../../../utils/settings';
import x from '../../../../../utils/store';
import { GetDealerCompanies } from '../../../../auth/action/util_actions';
import { getIsFMCG } from '../../../../auth/action';

const Sidebar = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [BenefitsActive, setBenefitsActive] = useState(false);
  const [benefitsTab, setBenefitsTab] = useState(UserTypeDict['DISTRIBUTOR']);

  const [hidePurschaseHistory, setHidePurschaseHistory] = useState(true);
  const isFMCG = getIsFMCG();

  const {
    loginUser,
    sidebar,
    secondaryPermissionsRoles,
    productPriceVisibility,
    certificates,
    dispatchTabStatus,
    orderTab,
    isSellToBuyer,
    isPaint
  } = useSelector((state) => {
    return {
      loginUser: state.loginReducer.loginUser,
      sidebar: state.distributorReducer.mobile_sidebar,
      showOrderTab: state.distributorReducer.showOrderTab,
      secondaryPermissionsRoles: state.loginReducer.secondaryPermissionsRoles,
      productPriceVisibility: state.orderReducer?.productPriceVisibility,
      certificates: state.loginReducer.certificates,
      dispatchTabStatus: state.orderReducer.dispatchStatusTab,
      orderTab: state.orderReducer.orderTab,
      isSellToBuyer: state.fmcgReducer.isSellToBuyer,
      isPaint: state.loginReducer.isPaint,
    };
  });

  if (!secondaryPermissionsRoles) {
    localStorage.clear();
    x.store.dispatch({ type: 'LOGOUT' });
  }

  const ORDER_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['ORDER_ADMIN'],
  );
  const PAYMENT_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['PAYMENT_ADMIN'],
  );
  const GIFT_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['GIFT_ADMIN'],
  );
  const INVOICE_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['INVOICE_ADMIN'],
  );
  const COMPANY_ADMIN = secondaryPermissionsRoles?.includes(
    secondaryPermission['COMPANY_ADMIN'],
  );

  const isGujaratUser = includesGujaratStateAndOther(loginUser);

  const isRetailer =
    loginUser &&
    loginUser.company_details &&
    loginUser.company_details.company_type === UserTypeDict['RETAILER'];
  const isAssociate =
    loginUser &&
    loginUser.company_details &&
    loginUser.company_details.company_type === UserTypeDict['ASSOCIATE'];
  const isDistributor =
    loginUser &&
    loginUser.company_details &&
    loginUser.company_details.company_type === UserTypeDict['DISTRIBUTOR'];
  const isDealerRetailer =
    loginUser &&
    loginUser.company_details &&
    loginUser.company_details.company_type === UserTypeDict['DEALER_RETAILER'];

  const thisPage = location.pathname;

  useEffect(() => {
    // Checking The User Active Page
    if (thisPage === '/distributor/Benefits') {
      setBenefitsActive(true);
    } else {
      setBenefitsActive(false);
    }

    // Checking The User Active Page
    if (
      [
        '/distributor/vieworders',
        '/distributor/viewordersdispatchCompleted',
      ].includes(thisPage)
    ) {
      dispatch({
        type: 'SET_DISPATCH_STATUS_TAB',
        data: {
          active: true,
          activeTabName:
            thisPage === '/distributor/vieworders' ? 'Pending' : 'Completed',
        },
      });
    } else {
      dispatch({
        type: 'SET_DISPATCH_STATUS_TAB',
        data: {
          active: false,
          activeTabName: 'Pending',
        },
      });
    }

    // Checking The User Active Page
    if (
      ['/distributor/orderby', '/distributor/SecondayOrders'].includes(thisPage)
    ) {
      dispatch({
        type: 'SET_ORDER_TAB',
        data: {
          active: true,
          activeTabName:
            thisPage === '/distributor/orderby' ? 'CreateOrder' : 'AllOrders',
        },
      });
    } else {
      dispatch({
        type: 'SET_ORDER_TAB',
        data: {
          active: false,
          activeTabName: 'AllOrders',
        },
      });
    }
  }, [thisPage]);

  const desktopSidbarUrlClasses = (url) => {
    return `sidebar-list-item ${
      thisPage === url ? 'sidebar-list-item-active' : ''
    }`;
  };

  const masonHandler = () => {
    dispatch({
      type: 'SET_BUYER',
      data: {
        buyerType: {
          type: UserTypeDict['MASON'],
          typeString: 'Mason',
        },
      },
    });
    checkOnboarding(loginUser, navigate, '/distributor/selltomason');
  };

  const retailerHandler = () => {
    dispatch({
      type: 'SET_BUYER',
      data: {
        buyerType: {
          type: UserTypeDict['RETAILER'],
          typeString: 'Retailer',
        },
      },
    });
    checkOnboarding(loginUser, navigate, '/distributor/selltoretailer');
  };

  const projectHandler = () => {
    dispatch({
      type: 'SET_BUYER',
      data: {
        buyerType: {
          type: UserTypeDict['PROJECT'],
          typeString: 'Project',
          isFromDispatchPlanning: false,
        },
      },
    });
    checkOnboarding(loginUser, navigate, '/distributor/sellToProject');
  };
  const associateHandler = () => {
    dispatch({
      type: 'SET_BUYER',
      data: {
        buyerType: {
          type: UserTypeDict['ASSOCIATE'],
          typeString: 'Associate',
          isFromDispatchPlanning: false,
        },
      },
    });
    checkOnboarding(loginUser, navigate, '/distributor/sellToAssociate');
  };

  const getAllCompanies = async () => {
    const { error, data, message } = await GetDealerCompanies({});

    if (!error) {
      data.forEach((item) => {
        if (Number(item.id) === Number(CompanyFactoryPrice[0])) {
          setHidePurschaseHistory(false);
        }
      });
    }
  };

  useEffect(() => {
    getAllCompanies();

    if (!orderTab) {
      localStorage.clear();
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  return (
    <div className='sidebars'>
      {/* Desktop Sidebar */}
      <div className='sidebar'>
        <div className='fixed__container'>
          <div className='sidebar__header'>Menu</div>
          <ul className='sidebar-list'>
            {/* Home */}
            <li
              onClick={() => {
                checkOnboarding(loginUser, navigate, '/distributor/dashboard');
              }}
              className={desktopSidbarUrlClasses('/distributor/dashboard')}
            >
              <HomeOutlined className='sidebar-list-logo' />
              <div> {t('distributor.home')}</div>
            </li>

            {/* FMCG & Stock */}
            <li
              onClick={() => {
                dispatch({ type: 'RESET_FMCG_REDUCER' });
                dispatch({ type: 'CLEAR_RETAILERS' });
                if (isFMCG) {
                  checkOnboarding(loginUser, navigate, '/fmcg/inventory');
                } else {
                  dispatch({
                    type: 'SHOW_COMPANY_SELECTION_MODAL',
                    showCompanySelectionModal: true,                  
                  })
                  dispatch({
                    type: 'IS_COMING_FROM_STOCK',
                    isComingFromStock: true,    
                  });
                  dispatch({
                    type: 'SET_IS_ONBOARDING_BUYER',
                    isOnboardingBuyer: false,
                  })
                  checkOnboarding(loginUser, navigate, '/distributor/dashboard');
                  // checkOnboarding(
                  //   loginUser,
                  //   navigate,
                  //   '/distributor/allinventory',
                  // );
                }
              }}
              className={desktopSidbarUrlClasses(
                !isSellToBuyer ? '/fmcg/inventory' : '/distributor/allinventory',
              )}>
              <StockOutlined className='sidebar-list-logo' />
              <div> {t('distributor.stock')}</div>
            </li>

            {/* Invoice */}
            {/* {!includesGujaratStateAndOther(loginUser) && (
              <li
                onClick={() => {
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/user-invoices',
                  );
                }}
                className={desktopSidbarUrlClasses(
                  '/distributor/user-invoices',
                )}
              >
                <OrderedListOutlined className='sidebar-list-logo' />
                <div> {t('distributor.invoices')}</div>
              </li>
            )} */}

            {/* Download */}
            {/* {certificates && certificates.length > 0 && (
              <li
                onClick={() => {
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/downloadInsurance',
                  );
                }}
                className={desktopSidbarUrlClasses(
                  '/distributor/downloadInsurance',
                )}
              >
                <DownloadOutlined className='sidebar-list-logo' />
                <div style={{ fontSize: '17px' }}>Download Insurance</div>
              </li>
            )} */}

            {/* Purchase Order/Order Booking */}
            {!isRetailer && !isAssociate && (COMPANY_ADMIN || ORDER_ADMIN) && (
              <li
                onClick={() => {
                  // isFMCG
                  //   ? 
                    checkOnboarding(
                        loginUser,
                        navigate,
                        '/distributor/OrderBookingViewController',
                      )
                    // : checkOnboarding(
                    //     loginUser,
                    //     navigate,
                    //     '/distributor/purchaseOrder',
                    //   );
                }}
                className={
                  isFMCG
                    ? desktopSidbarUrlClasses(
                        '/distributor/OrderBookingViewController',
                      )
                    : desktopSidbarUrlClasses('/distributor/purchaseOrder')
                }
              >
                <OrderedListOutlined className='sidebar-list-logo' />
                <div>Order Booking</div>
              </li>
            )}

            {/* Dispatch Planning */}
            {!isRetailer &&
              !isAssociate &&
              !isFMCG &&
              !isPaint &&
              (COMPANY_ADMIN || ORDER_ADMIN) && (
                <li
                  className={desktopSidbarUrlClasses(
                    '/distributor/orderBooking',
                  )}
                  onClick={() => {
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/orderBooking',
                    );
                    // setDispatchPlanningTab('List');
                    // setDispatchPlanningAct(!dispatchPlanningAct);
                  }}
                >
                  <OrderedListOutlined className='sidebar-list-logo' />
                  Dispatch Planning
                </li>
              )}

            {/* Dispatch Planning Report
            {!isRetailer && !isAssociate && (COMPANY_ADMIN || ORDER_ADMIN) && (
              <li
                className={desktopSidbarUrlClasses(
                  '/distributor/dipatchPlanningReport',
                )}
                onClick={() => {
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/dipatchPlanningReport',
                  );
                }}
              >
                <OrderedListOutlined className='sidebar-list-logo' />
                Dispatch Planning Report
              </li>
            )} */}

            {/* Dispatch Status */}
            {!isRetailer && !isAssociate && (COMPANY_ADMIN || ORDER_ADMIN) && (
              <>
                <li
                  className={`sidebar-list-item ${
                    [
                      '/distributor/vieworders',
                      '/distributor/viewordersdispatchCompleted',
                    ].includes(thisPage)
                      ? ' sidebar-list-item-active'
                      : ''
                  }`}
                  onClick={() => {
                    dispatch({
                      type: 'SET_DISPATCH_STATUS_TAB',
                      data: {
                        active: !dispatchTabStatus?.active,
                        activeTabName: 'Pending',
                      },
                    });
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/vieworders',
                    );
                  }}
                >
                  <ShoppingCartOutlined className='sidebar-list-logo' />{' '}
                  Dispatch Status
                </li>
                <ul
                  className={`sidebar_submenu_list  ${
                    dispatchTabStatus?.active && 'active'
                  }`}
                >
                  {/* Pending Dispatch */}
                  <li
                    onClick={() => {
                      dispatch({
                        type: 'SET_DISPATCH_STATUS_TAB',
                        data: { active: true, activeTabName: 'Pending' },
                      });
                      navigate('/distributor/vieworders');
                    }}
                    className={`${
                      dispatchTabStatus?.activeTabName === 'Pending' &&
                      'item-active'
                    }`}
                  >
                    <ShoppingCartOutlined className='sidebar-list-logo' />{' '}
                    Pending Dispatch
                  </li>

                  {/* Partial Delivered */}
                  <li
                    onClick={() => {
                      dispatch({
                        type: 'SET_DISPATCH_STATUS_TAB',
                        data: { active: true, activeTabName: 'Partial' },
                      });
                      navigate('/distributor/vieworders');
                    }}
                    className={`${
                      dispatchTabStatus?.activeTabName === 'Partial' &&
                      'item-active'
                    }`}
                  >
                    <ShoppingCartOutlined className='sidebar-list-logo' />{' '}
                    Partial Delivered
                  </li>

                  {/* Complete Dispatch */}
                  <li
                    onClick={() => {
                      dispatch({
                        type: 'SET_DISPATCH_STATUS_TAB',
                        data: { active: true, activeTabName: 'Completed' },
                      });
                      navigate('/distributor/viewordersdispatchCompleted');
                    }}
                    className={`${
                      dispatchTabStatus?.activeTabName === 'Completed' &&
                      'item-active'
                    }`}
                  >
                    <ShoppingCartOutlined className='sidebar-list-logo' />{' '}
                    Completed Dispatch
                  </li>
                </ul>
              </>
            )}

            {/* Order */}
            {(COMPANY_ADMIN || ORDER_ADMIN) && (
              <>
                <li
                  className={`sidebar-list-item ${
                    [
                      '/distributor/orderby',
                      '/distributor/SecondayOrders',
                    ].includes(thisPage)
                      ? ' sidebar-list-item-active'
                      : ''
                  }`}
                  onClick={() => {
                    dispatch({
                      type: 'SET_ORDER_TAB',
                      data: {
                        active: !orderTab?.active,
                        activeTabName: 'AllOrders',
                      },
                    });
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/SecondayOrders',
                    );
                  }}
                >
                  <ShoppingCartOutlined className='sidebar-list-logo' /> Order
                </li>
                <ul
                  className={`sidebar_submenu_list  ${
                    orderTab?.active && 'active'
                  }`}
                >
                  {/* Create Order */}
                  {(isRetailer || isAssociate) && (
                    <li
                      onClick={() => {
                        dispatch({
                          type: 'SET_ORDER_TAB',
                          data: { active: true, activeTabName: 'CreateOrder' },
                        });
                        navigate('/distributor/orderby');
                      }}
                      className={`${
                        orderTab?.activeTabName === 'CreateOrder' &&
                        'item-active'
                      }`}
                    >
                      <ShoppingCartOutlined className='sidebar-list-logo' />{' '}
                      Create Order
                    </li>
                  )}

                  {/* Secondary Orders */}
                  <li
                    onClick={() => {
                      dispatch({
                        type: 'SET_ORDER_TAB',
                        data: { active: true, activeTabName: 'AllOrders' },
                      });
                      navigate('/distributor/SecondayOrders');
                    }}
                    className={`${
                      orderTab?.activeTabName === 'AllOrders' && 'item-active'
                    }`}
                  >
                    <ShoppingCartOutlined className='sidebar-list-logo' />{' '}
                    {isRetailer || isAssociate ? 'Orders' : 'Secondary Orders'}
                  </li>
                </ul>
              </>
            )}

            {/* Order */}
            {/* {(isRetailer || isAssociate) && (
              <li
                onClick={() => {
                  checkOnboarding(loginUser, navigate, '/distributor/orderby');
                }}
                className={desktopSidbarUrlClasses('/distributor/orderby')}
              >
                <OrderedListOutlined className='sidebar-list-logo' />
                <div>Order</div>
              </li>
            )} */}

            {/* Secondary Orders */}
            {/* {(isRetailer || isAssociate || isDistributor) && (
              <li
                onClick={() => {
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/SecondayOrders',
                  );
                }}
                className={desktopSidbarUrlClasses(
                  '/distributor/SecondayOrders',
                )}
              >
                <OrderedListOutlined className='sidebar-list-logo' />
                <div>Secondary Orders</div>
              </li>
            )} */}

            {/* Payment */}
            {!isRetailer &&
              !isAssociate &&
              (COMPANY_ADMIN || PAYMENT_ADMIN) && (
                <li
                  onClick={() => {
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/payment',
                    );
                  }}
                  className={desktopSidbarUrlClasses('/distributor/payment')}
                >
                  <CreditCardOutlined className='sidebar-list-logo' />{' '}
                  {'Payment'}
                </li>
              )}

            {/* Ledger Balance */}
            {/* {!isRetailer &&
              !isAssociate &&
              (COMPANY_ADMIN || PAYMENT_ADMIN) &&
              !isGujaratUser && (
                <li
                  onClick={() => {
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/ledgerBalance',
                    );
                  }}
                  className={desktopSidbarUrlClasses(
                    '/distributor/ledgerBalance',
                  )}
                >
                  <WalletOutlined className='sidebar-list-logo' />{' '}
                  {'Ledger Balance'}
                </li>
              )} */}

            {/* Payment History */}
            {!isRetailer &&
              !isAssociate &&
              (COMPANY_ADMIN || PAYMENT_ADMIN) && (
                <li
                  onClick={() => {
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/paymentHistory',
                    );
                  }}
                  className={desktopSidbarUrlClasses(
                    '/distributor/paymentHistory',
                  )}
                >
                  <WalletOutlined className='sidebar-list-logo' />{' '}
                  {'Payment History'}
                </li>
              )}

            <li
              onClick={() => {
                checkOnboarding(
                  loginUser,
                  navigate,
                  '/distributor/userReports',
                );
              }}
              className={desktopSidbarUrlClasses('/distributor/userReports')}
            >
              <WalletOutlined className='sidebar-list-logo' /> {'Report'}
            </li>

            {/* Rewards */}
            <li
              className={desktopSidbarUrlClasses('/distributor/gifts')}
              onClick={() => {
                checkOnboarding(loginUser, navigate, '/distributor/gifts');
              }}
            >
              <GiftOutlined className='sidebar-list-logo' /> {t('gift')}
            </li>

            {/* Product Pricing */}
            {!isFMCG && !isPaint &&
            ((isRetailer && productPriceVisibility) ||
              (isAssociate && productPriceVisibility) ||
              isDealerRetailer ||
              isDistributor) ? (
              <li
                className={desktopSidbarUrlClasses(
                  '/distributor/productPricing',
                )}
                onClick={() => {
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/productPricing',
                  );
                }}
              >
                <DollarOutlined className='sidebar-list-logo' /> Product Pricing
              </li>
            ) : (
              ''
            )}

            {/* Sell Invoice */}
            <li
              className={desktopSidbarUrlClasses('/distributor/sellinvoices')}
              onClick={() => {
                checkOnboarding(
                  loginUser,
                  navigate,
                  '/distributor/sellinvoices',
                );
              }}
            >
              <GiftOutlined className='sidebar-list-logo' /> {t('sellInvoices')}
            </li>

            {/* Humbee Benefits */}
            {
              <>
                <li
                  className={desktopSidbarUrlClasses('/distributor/Benefits')}
                  onClick={() => {
                    setBenefitsActive(!BenefitsActive);
                    if (isDistributor || isDealerRetailer) {
                      dispatch({
                        type: 'BENEFITS_TYPE_CHANGE',
                        data: UserTypeDict['DISTRIBUTOR'],
                      });
                    } else if (isRetailer) {
                      dispatch({
                        type: 'BENEFITS_TYPE_CHANGE',
                        data: UserTypeDict['RETAILER'],
                      });
                    } else if (isAssociate) {
                      dispatch({
                        type: 'BENEFITS_TYPE_CHANGE',
                        data: UserTypeDict['ASSOCIATE'],
                      });
                    }
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/Benefits',
                    );
                  }}
                >
                  <BiMedal className='sidebar-list-logo' />{' '}
                  {t('humbeeBenefits')}
                </li>
                <ul
                  className={`sidebar_submenu_list  ${
                    BenefitsActive && 'active'
                  }`}
                >
                  {(isDistributor || isDealerRetailer) && (
                    <li
                      onClick={() => {
                        setBenefitsTab(UserTypeDict['DISTRIBUTOR']);
                        dispatch({
                          type: 'BENEFITS_TYPE_CHANGE',
                          data: UserTypeDict['DISTRIBUTOR'],
                        });
                      }}
                      className={`${
                        benefitsTab === UserTypeDict['DISTRIBUTOR'] &&
                        'item-active'
                      }`}
                    >
                      <UserOutlined /> {t('onboarding.distibutor')}
                    </li>
                  )}
                  {(isRetailer || isDistributor || isDealerRetailer) && (
                    <li
                      onClick={() => {
                        setBenefitsTab(UserTypeDict['RETAILER']);
                        dispatch({
                          type: 'BENEFITS_TYPE_CHANGE',
                          data: UserTypeDict['RETAILER'],
                        });
                      }}
                      className={`${
                        benefitsTab === UserTypeDict['RETAILER'] &&
                        'item-active'
                      }`}
                    >
                      <UserOutlined /> {t('retailer')}
                    </li>
                  )}
                  {!isFMCG &&
                    (isAssociate || isDistributor || isDealerRetailer) && (
                      <li
                        onClick={() => {
                          setBenefitsTab(UserTypeDict['ASSOCIATE']);
                          dispatch({
                            type: 'BENEFITS_TYPE_CHANGE',
                            data: UserTypeDict['ASSOCIATE'],
                          });
                        }}
                        className={`${
                          benefitsTab === UserTypeDict['ASSOCIATE'] &&
                          'item-active'
                        }`}
                      >
                        <UserOutlined /> Associate
                      </li>
                    )}
                </ul>
              </>
            }
            {/* --------- */}

            {/* My Benefits */}
            {/* {!includesGujaratStateAndOther(loginUser) && ( */}
            <li
              className={desktopSidbarUrlClasses('/distributor/mybenefits')}
              onClick={() => {
                checkOnboarding(loginUser, navigate, '/distributor/mybenefits');
              }}
            >
              <BiMedal className='sidebar-list-logo' />
              My Benefits
            </li>
            {/* )} */}

            {/* All Benefits */}
            {!isFMCG && (
              <li
                className={desktopSidbarUrlClasses('/distributor/allbenefits')}
                onClick={() => {
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/allbenefits',
                  );
                }}
              >
                <BiMedal className='sidebar-list-logo' />
                All Benefits
              </li>
            )}


            {/* Onboard Buyer */}
            {
              <li
                onClick={() => {
                  if(isFMCG){
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/oboardBuyers',
                    );
                  } else {
                    dispatch({
                      type: 'SHOW_COMPANY_SELECTION_MODAL',
                      showCompanySelectionModal: true,                    
                    })
                    dispatch({
                      type: 'IS_COMING_FROM_STOCK',
                      isComingFromStock: false,    
                    });
                    dispatch({
                      type: 'SET_IS_ONBOARDING_BUYER',
                      isOnboardingBuyer: true,
                    })
                    checkOnboarding(loginUser, navigate, '/distributor/dashboard');
                  }
                  
                }}
                className={desktopSidbarUrlClasses(

                  '/distributor/oboardBuyers',
                )}>
                <ShoppingCartOutlined className='sidebar-list-logo' />
                <div>Onboard Buyer</div>

              </li>
            }


            {/* Sell to buyer */}
              <li
                onClick={() => {
                  if(isFMCG){
                    checkOnboarding(loginUser, navigate, '/fmcg/inventory');
                    dispatch({ type: 'FMCG_IS_SELL_TO_BUYER', data: true });
                  } else {
                    dispatch({
                      type: 'SHOW_COMPANY_SELECTION_MODAL',
                      showCompanySelectionModal: true,                    
                    })
                    dispatch({
                      type: 'IS_COMING_FROM_STOCK',
                      isComingFromStock: false,    
                    });
                    dispatch({
                      type: 'SET_IS_ONBOARDING_BUYER',
                      isOnboardingBuyer: false,
                    })
                    checkOnboarding(loginUser, navigate, '/distributor/dashboard');
                  }
                 
                }}
                className={  desktopSidbarUrlClasses(
                  isSellToBuyer && '/fmcg/inventory',
                )}>

                <ShoppingCartOutlined className='sidebar-list-logo' />
                <div>Sell to buyer</div>
              </li>

            {/* Settings */}
            <li
              onClick={() => {
                checkOnboarding(loginUser, navigate, '/profile');
              }}
              className={desktopSidbarUrlClasses('/profile')}
            >
              <ShoppingCartOutlined className='sidebar-list-logo' />
              <div> {t('distributor.settings')}</div>
            </li>
          </ul>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div className={`mobile_sidebar ${sidebar && 'active'} `}>
        <div className='mobile_sidebar-close displayCenter'>
          <CloseOutlined
            onClick={() => dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' })}
          />
        </div>
        <div className='mobile_sidebar_list-container'>
          <ul className='mobile_sidebar_list'>
            {/* Home */}
            <li
              className={`mobile_sidebar_list-item ${
                thisPage === '/distributor/dashboard' ? 'active' : ''
              }`}
              onClick={() => {
                dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                dispatch({
                  type: 'IS_COMING_FROM_STOCK',
                  isComingFromStock: false,    
                });
                checkOnboarding(loginUser, navigate, '/distributor/dashboard');
              }}
            >
              <HomeOutlined /> {t('distributor.home')}
            </li>

            {/* Stock */}
            <li
              className={`mobile_sidebar_list-item ${
                thisPage === '/fmcg/inventory'
                  ? 'active'
                  : thisPage === '/distributor/allinventory'
                  ? 'active'
                  : ''
              }`}
              onClick={() => {
                dispatch({ type: 'RESET_FMCG_REDUCER' });
                dispatch({ type: 'CLEAR_RETAILERS' });

                if (isFMCG) {
                  checkOnboarding(loginUser, navigate, '/fmcg/inventory');
                } else {
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/allinventory',
                  );
                }
              }}
            >
              <StockOutlined /> {t('distributor.stock')}
            </li>

            {/* Invoices */}
            {!includesGujaratStateAndOther(loginUser) && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/user-invoices' ? 'active' : ''
                }`}
                onClick={() => {
                  dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/user-invoices',
                  );
                }}
              >
                <OrderedListOutlined /> {t('distributor.invoices')}
              </li>
            )}

            {/* Purchase Order */}
            {!isRetailer && !isAssociate && (COMPANY_ADMIN || ORDER_ADMIN) && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/purchaseOrder' ? 'active' : ''
                }`}
                onClick={() => {
                  dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/purchaseOrder',
                  );
                }}
              >
                <OrderedListOutlined /> Purchase Order
              </li>
            )}

            {/* Dispatch Planning */}
            {!isRetailer && !isAssociate && (COMPANY_ADMIN || ORDER_ADMIN) && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/orderBooking' ? 'active' : ''
                }`}
                onClick={() => {
                  dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/orderBooking',
                  );
                }}
              >
                <OrderedListOutlined /> Dispatch Planning
              </li>
            )}

            {/* Dispatch Status */}
            {!isRetailer && !isAssociate && (COMPANY_ADMIN || ORDER_ADMIN) && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/vieworders' ? 'active' : ''
                }`}
                onClick={() => {
                  dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/vieworders',
                  );
                }}
              >
                <ShoppingCartOutlined className='sidebar-list-logo' /> Dispatch
                Status
              </li>
            )}

            {/* Dispatch Planning Report
            {!isRetailer && !isAssociate && (COMPANY_ADMIN || ORDER_ADMIN) && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/dipatchPlanningReport'
                    ? 'active'
                    : ''
                }`}
                onClick={() => {
                  dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/dipatchPlanningReport',
                  );
                }}
              >
                <OrderedListOutlined className='sidebar-list-logo' /> Dispatch
                Planning Report
              </li>
            )} */}

            {/* Payment */}
            {!isRetailer &&
              !isAssociate &&
              (COMPANY_ADMIN || PAYMENT_ADMIN) && (
                <li
                  className={`mobile_sidebar_list-item ${
                    thisPage === '/distributor/payment' ? 'active' : ''
                  }`}
                  onClick={() => {
                    dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/payment',
                    );
                  }}
                >
                  <CreditCardOutlined className='sidebar-list-logo' />{' '}
                  {'Payment'}
                </li>
              )}

            {/* Ledger Balance */}
            {!isRetailer &&
              !isAssociate &&
              (COMPANY_ADMIN || PAYMENT_ADMIN) &&
              !isGujaratUser && (
                <li
                  className={`mobile_sidebar_list-item ${
                    thisPage === '/distributor/ledgerBalance' ? 'active' : ''
                  }`}
                  onClick={() => {
                    dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/ledgerBalance',
                    );
                  }}
                >
                  <WalletOutlined /> Ledger Balance
                </li>
              )}

            {/* Rewards */}
            <li
              className={`mobile_sidebar_list-item ${
                thisPage === '/distributor/gifts' ? 'active' : ''
              }`}
              onClick={() => {
                dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });

                checkOnboarding(loginUser, navigate, '/distributor/gifts');
              }}
            >
              <GiftOutlined /> {t('gift')}
            </li>

            {/* Product Pricing */}
            <li
              className={`mobile_sidebar_list-item ${
                thisPage === '/distributor/productPricing' ? 'active' : ''
              }`}
              onClick={() => {
                dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                checkOnboarding(
                  loginUser,
                  navigate,
                  '/distributor/productPricing',
                );
              }}
            >
              <DollarOutlined className='sidebar-list-logo' /> Product Pricing
            </li>

            {/* Sell Invoices */}
            <li
              className={`mobile_sidebar_list-item ${
                thisPage === '/distributor/sellinvoices' ? 'active' : ''
              }`}
              onClick={() => {
                dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                checkOnboarding(
                  loginUser,
                  navigate,
                  '/distributor/sellinvoices',
                );
              }}
            >
              <GiftOutlined className='sidebar-list-logo' /> {t('sellInvoices')}
            </li>

            {/* Humbee Benefits */}
            {
              <>
                <li
                  className={`mobile_sidebar_list-item ${
                    thisPage === '/distributor/Benefits' ? 'active' : ''
                  }`}
                  onClick={() => {
                    checkOnboarding(
                      loginUser,
                      navigate,
                      '/distributor/Benefits',
                    );
                    setBenefitsActive(!BenefitsActive);
                  }}
                >
                  <BiMedal />
                  {t('humbeeBenefits')}
                </li>
                <ul
                  className={`sidebar_submenu_list  ${
                    BenefitsActive && 'active'
                  }`}
                  style={{ marginBottom: 16 }}
                >
                  {!isRetailer && !isAssociate && (
                    <li
                      onClick={() => {
                        setBenefitsTab(UserTypeDict['DISTRIBUTOR']);

                        dispatch({
                          type: 'BENEFITS_TYPE_CHANGE',
                          data: UserTypeDict['DISTRIBUTOR'],
                        });
                        dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                      }}
                      className={`${
                        benefitsTab === UserTypeDict['DISTRIBUTOR'] &&
                        'item-active'
                      }`}
                    >
                      <UserOutlined /> {t('onboarding.distibutor')}
                    </li>
                  )}
                  <li
                    onClick={() => {
                      setBenefitsTab(UserTypeDict['RETAILER']);
                      dispatch({
                        type: 'BENEFITS_TYPE_CHANGE',
                        data: UserTypeDict['RETAILER'],
                      });
                      dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                    }}
                    className={`${
                      benefitsTab === UserTypeDict['RETAILER'] && 'item-active'
                    }`}
                  >
                    <UserOutlined /> {t('retailer')}
                  </li>
                </ul>
              </>
            }

            {/* My Benefits*/}
            {!includesGujaratStateAndOther(loginUser) && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/mybenefits' ? 'active' : ''
                }`}
                onClick={() => {
                  dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                  checkOnboarding(
                    loginUser,
                    navigate,
                    '/distributor/mybenefits',
                  );
                }}
              >
                <BiMedal />
                My Benefits
              </li>
            )}

            {/* Shilpkar */}
            {!isFMCG && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/selltomason' ? 'active' : ''
                }`}
                onClick={masonHandler}
              >
                {' '}
                <UserSwitchOutlined /> {t('distributor.mason')}
              </li>
            )}

            {/* Reatiler */}

            {!isRetailer && !isAssociate && (
              <li
                className={`mobile_sidebar_list-item ${
                  thisPage === '/distributor/selltoretailer' ? 'active' : ''
                }`}
                onClick={retailerHandler}
              >
                {' '}
                <UserSwitchOutlined /> {t('distributor.retailer')}
              </li>
            )}

            {/* Settings */}
            <li
              className={`mobile_sidebar_list-item ${
                thisPage === '/profile' ? 'active' : ''
              }`}
              onClick={() => {
                dispatch({ type: 'TOGGLE_MOBILE_SIDEBAR' });
                checkOnboarding(loginUser, navigate, '/profile');
              }}
            >
              {' '}
              <FiSettings /> {t('distributor.settings')}
            </li>
          </ul>
        </div>
      </div>
      {/* ------------ */}
    </div>
  );
};

export default Sidebar;
